import { useCallback, useState, useMemo } from "react";
import { isEmpty } from "lodash";
import EditIcon from "@mui/icons-material/Edit";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteIcon from "@mui/icons-material/Delete";

import Button, { ButtonTypes } from "../../../components/Button";
import {
  SavingHero,
  SavingProgressChart,
  ProgressTable,
  buildProgressRows,
  progressCountsOf,
  DeleteSavingModal,
  DeleteProgressModal,
  StatusChangeModal,
  SavingSkeleton,
  SavingHeroFigures,
} from "./components";
import {
  useSavingData,
  useSavingModals,
  useProgressPanel,
  useSavingActions,
  useExpensePanel,
} from "./hooks";
import { SavingDetailsWrapper, Wrapper } from "./styles";
import { SavingPageProps } from "./types";
import {
  addCashbackDisabledReasonOf,
  balanceMovingRowsOf,
  buildSavingProgress,
  buildValueComposition,
  cashbackProgressOf,
  getProgressObject,
  netContributedBefore,
} from "./utils";
import { WindowType } from "../../../utils/types";
import { usePersistedWindow } from "../../../hooks/usePersistedWindow";
import { formatDate, TransactionType } from "../../../utils";
import { cashbackFormValues, cashbackPayload } from "../../../utils/cashback";
import { notify } from "../../../components/Notification";
import { useUpdateSavingMutation } from "../../../services";
import { SavingsTransactions, UpdateSavingsPayload } from "../types";
import { DropdownAction } from "../../../components/Dropdown/types";
import { EmptyState, ErrorState } from "../../../components/States";
import {
  ProgressObject,
  ProgressPanel,
  SavingFormValues,
  SavingsPanel,
} from "../panels";
import {
  getTitleFromAction,
  PanelAction,
  TransactionsPanel,
} from "../../Transactions";
import { toSelectOption } from "../../../components/Input";
import {
  CorrectionDeletePopUp,
  useCorrectionDelete,
} from "../../../components/CashbackSection";

export const SavingPage: React.FC<SavingPageProps> = ({ id }) => {
  // Named to avoid shadowing the global `window`.
  const [requestedWindow, setTimeWindow] = usePersistedWindow(
    "savingTimeWindow",
    WindowType.Y1,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [updateSaving] = useUpdateSavingMutation();

  // Custom hooks
  const {
    window: timeWindow,
    windowOptions,
    saving,
    lifetimeSaving,
    savingUpdate,
    categoriesIncome,
    categoriesExpense,
    isLoadingIncomeCategories,
    hasIncomeCategoriesError,
    isLoading,
    error,
    notFoundError,
    lastUpdated,
    refetch: refetchSaving,
  } = useSavingData({ id, window: requestedWindow });

  const modals = useSavingModals();

  const {
    panel,
    progressToDelete,
    setProgressToDelete,
    openPanel,
    closePanel,
    getPanelTitle,
  } = useProgressPanel();

  const expensePanel = useExpensePanel({ refetchSaving });
  const correctionDelete = useCorrectionDelete({
    accountId: saving.accountId,
    onDeleted: () => void refetchSaving(),
  });

  const actions = useSavingActions({
    id,
    saving,
    refetchSaving,
    openDeleteModal: modals.openDeleteModal,
    openProgressDeleteModal: modals.openProgressDeleteModal,
    openActivateModal: modals.openActivateModal,
    closeDeleteModal: modals.closeDeleteModal,
    closeProgressDeleteModal: modals.closeProgressDeleteModal,
    closeActivateModal: modals.closeActivateModal,
    progressToDelete,
    setProgressToDelete,
    closePanel,
  });

  const savingFormValue: SavingFormValues = {
    ...cashbackFormValues(savingUpdate),
    name: savingUpdate.name ?? "",
    institution: savingUpdate.institution,
    startDate: savingUpdate.startDate ?? new Date(),
  };

  // The hero and the Progress table describe the saving's whole life; only the chart follows the selected window.
  const lifetimeProgress = useMemo(
    () => buildSavingProgress(lifetimeSaving),
    [lifetimeSaving],
  );
  const progress = useMemo(
    () =>
      buildSavingProgress(
        saving,
        netContributedBefore(lifetimeProgress.chartPoints, saving),
      ),
    [saving, lifetimeProgress],
  );
  const progressRows = useMemo(
    () => buildProgressRows(lifetimeSaving, lifetimeProgress.entries),
    [lifetimeSaving, lifetimeProgress],
  );
  const progressCounts = useMemo(
    () => progressCountsOf(progressRows),
    [progressRows],
  );
  const heroFigures = useMemo<SavingHeroFigures>(
    () => ({
      ...buildValueComposition(saving, lifetimeProgress),
      interestWithdrawn: lifetimeProgress.interestWithdrawn,
      cashbackWithdrawn: lifetimeProgress.cashbackWithdrawn,
    }),
    [saving, lifetimeProgress],
  );

  const { openExpensePanel } = expensePanel;
  const { openProgressDeleteModal } = modals;
  const openEntryPanel = useCallback(
    (action: PanelAction, transaction: SavingsTransactions) => {
      if (transaction.type === TransactionType.EXPENSE) {
        void openExpensePanel(action, transaction.id);
        return;
      }
      openPanel(action, getProgressObject(transaction));
    },
    [openExpensePanel, openPanel],
  );
  const editEntry = useCallback(
    (transaction: SavingsTransactions) => openEntryPanel("edit", transaction),
    [openEntryPanel],
  );
  const duplicateEntry = useCallback(
    (transaction: SavingsTransactions) =>
      openEntryPanel("duplicate", transaction),
    [openEntryPanel],
  );
  const openExpense = useCallback(
    (expenseId: number) => void openExpensePanel("edit", expenseId),
    [openExpensePanel],
  );
  const addCashback = useCallback(() => {
    const cashbackProgress = cashbackProgressOf(saving, categoriesIncome);
    if (cashbackProgress) {
      openPanel("create", cashbackProgress);
    }
  }, [openPanel, saving, categoriesIncome]);
  const addCashbackDisabledReason = addCashbackDisabledReasonOf({
    isLoading: isLoadingIncomeCategories,
    hasError: hasIncomeCategoriesError,
    categories: categoriesIncome,
  });
  const deleteEntry = useCallback(
    (transaction: SavingsTransactions) => {
      setProgressToDelete(transaction);
      openProgressDeleteModal();
    },
    [setProgressToDelete, openProgressDeleteModal],
  );

  const handleUpdateSaving = async (values: SavingFormValues) => {
    const payload: UpdateSavingsPayload = {
      ...cashbackPayload(values),
      name: values.name,
      institution: values.institution,
      startDate: formatDate(values.startDate),
    };
    try {
      await updateSaving({ id: savingUpdate.id, payload }).unwrap();
      notify.success("Saving updated!");
      setIsOpen(false);
    } catch (err: any) {
      notify.apiError(err);
    }
  };

  // Memoize dropdown actions to prevent unnecessary re-renders
  const dropdownActions = useMemo<DropdownAction[]>(
    () => [
      {
        id: "edit",
        label: "Edit saving",
        icon: <EditIcon fontSize="small" />,
        onClick: () => setIsOpen(true),
        show: true,
        tooltip: "Edit saving details (Shortcut: E)",
      },
      {
        id: "deactivate",
        label: "Deactivate",
        icon: <PauseIcon fontSize="small" />,
        onClick: () => {
          modals.openActivateModal();
          void actions.fetchDeletionImpact();
        },
        show: saving.isActive,
        tooltip: "Temporarily deactivate this saving",
      },
      {
        id: "activate",
        label: "Reactivate",
        icon: <PlayArrowIcon fontSize="small" />,
        onClick: modals.openActivateModal,
        show: !saving.isActive,
        tooltip: "Reactivate this saving",
      },
      {
        id: "delete",
        label: "Delete saving",
        icon: <DeleteIcon fontSize="small" />,
        onClick: () => {
          void (async () => {
            await actions.fetchDeletionImpact();
            actions.handleDelete();
          })();
        },
        variant: "destructive" as const,
        show: true,
        tooltip: "Delete this saving (Shortcut: D)",
      },
    ],
    [saving, modals, actions],
  );

  // The hero's net contributed needs the lifetime payload too; without it the uncorrected figure would render.
  const hasSaving = Boolean(saving.id) && Boolean(lifetimeSaving.id);

  if (isLoading && !hasSaving) {
    return <SavingSkeleton />;
  }

  if (!isLoading && notFoundError) {
    return (
      <Wrapper>
        <EmptyState
          title="Saving not found"
          description="The saving you're looking for doesn't exist."
          action={
            <Button
              size="large"
              type={ButtonTypes.primary}
              onClick={() => (window.location.href = "/savings")}
            >
              Back to Savings
            </Button>
          }
        />
      </Wrapper>
    );
  }

  if (error && !hasSaving) {
    return (
      <Wrapper>
        <ErrorState message={error} onRetry={() => refetchSaving()} />
      </Wrapper>
    );
  }

  const hasTransactions = balanceMovingRowsOf(lifetimeSaving).length > 0;

  return (
    <Wrapper className="fade-in" role="main" id="main-content">
      <StatusChangeModal
        isOpen={modals.activateModal}
        onClose={modals.closeActivateModal}
        onConfirm={actions.handleOnStatusChangeClick}
        saving={lifetimeSaving}
        linkedBrokers={actions.deletionImpact?.linkedBrokers}
        isCheckingLinkedBrokers={actions.isLoadingImpact}
      />
      <DeleteSavingModal
        isOpen={modals.deleteModal}
        onClose={modals.closeDeleteModal}
        onConfirm={actions.handleOnDeleteClick}
        savingName={saving.name}
        deletionImpact={actions.deletionImpact}
        isLoadingImpact={actions.isLoadingImpact}
        isDeleting={actions.isDeleting}
        deleteError={actions.deleteError}
      />
      <DeleteProgressModal
        isOpen={modals.progressDeleteModal}
        onClose={modals.closeProgressDeleteModal}
        onConfirm={actions.handleDeleteProgress}
        transaction={progressToDelete}
      />

      <SavingDetailsWrapper>
        <SavingHero
          saving={saving}
          figures={heroFigures}
          counts={progressCounts}
          hasTransactions={hasTransactions}
          lastUpdated={lastUpdated}
          dropdownActions={dropdownActions}
          onAddProgress={() => openPanel("create")}
          onAddCashback={addCashback}
          addCashbackDisabledReason={addCashbackDisabledReason}
        />
      </SavingDetailsWrapper>

      {hasTransactions && (
        <>
          <SavingProgressChart
            points={progress.chartPoints}
            window={timeWindow}
            options={windowOptions}
            onWindowChange={setTimeWindow}
            hasRetainedCashback={heroFigures.retainedCashback > 0}
          />

          <ProgressTable
            rows={progressRows}
            onEdit={editEntry}
            onDuplicate={duplicateEntry}
            onDelete={deleteEntry}
            onDeleteCorrection={correctionDelete.requestDelete}
            onOpenExpense={openExpense}
          />
        </>
      )}

      {!isEmpty(savingUpdate) && (
        <SavingsPanel
          saving={savingFormValue}
          handleOnSubmit={handleUpdateSaving}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
      {panel.isOpen && (
        <ProgressPanel
          title={getPanelTitle()}
          progress={panel.progress}
          accountId={saving.accountId}
          accountName={saving.name}
          investmentId={id}
          incomeCategoryList={categoriesIncome}
          expenseCategoryList={categoriesExpense}
          handleOnSubmit={(data: ProgressObject) =>
            actions.handleSubmitProgress(data, panel.action)
          }
          isOpen={panel.isOpen}
          onClose={closePanel}
        />
      )}
      <CorrectionDeletePopUp correctionDelete={correctionDelete} />
      {expensePanel.panel.isOpen && (
        <TransactionsPanel
          title={getTitleFromAction(expensePanel.panel.action)}
          action={expensePanel.panel.action}
          isOpen={expensePanel.panel.isOpen}
          incomeCategoryList={categoriesIncome}
          expenseCategoryList={categoriesExpense}
          onClose={expensePanel.closePanel}
          transaction={expensePanel.panel.transaction}
          handleOnSubmit={expensePanel.handleSubmit}
          defaultAccount={toSelectOption(saving.accountId, saving.name)}
        />
      )}
    </Wrapper>
  );
};
